import {
	MinusCircleOutlined,
	PlusOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Form,
	Input,
	message,
	Select,
	Space,
	Upload,
} from 'antd';
import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import { SSO } from '../_shared/index.js';

const { Option } = Select;

const attributeOptions = [
	{ id: 'custom:country', label: 'Country' },
	{ id: 'custom:department', label: 'Department' },
	{ id: 'email', label: 'Email' },
	{ id: 'custom:employeeid', label: 'Employee ID' },
	{ id: 'given_name', label: 'First Name' },
	{ id: 'family_name', label: 'Last Name' },
	{ id: 'custom:jobtitle', label: 'Job Title' },
	{ id: 'custom:subCompany', label: 'Sub Company' },
	{ id: 'custom:usergroupalt', label: 'User Group' },
];
let defaultKeys = {
	email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
	family_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
	given_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
};
class SettingsSSOForm extends Component {
	constructor(props) {
		super(props);
		this.state = {
			domain: this.props?.company?.host,
			setFolderName: false,
			xmlContent: '',
		};
		this.formRef = React.createRef();
	}

	async componentDidMount() {
		const ssoConfig = await SSO({
			companyId: this.props?.currentUser?.companyId,
			type: 'getSSO',
		});

		if (ssoConfig && ssoConfig[0]?.attributeMapping) {
			// Update defaultKeys with the saved mappings from ssoConfig
			defaultKeys = ssoConfig[0].attributeMapping;

			// Get the keys from the saved attributeMapping
			const keys = Object.keys(defaultKeys);

			// Set the form's keys field
			this.formRef.current?.setFieldsValue({ keys });

			// Set each attribute mapping field
			for (const [index, key] of keys.entries()) {
				this.formRef.current?.setFieldsValue({
					[`attributeMapping[${index}].key`]: key,
					[`attributeMapping[${index}].value`]: defaultKeys[key],
				});
			}

			this.setState({
				companyCode: ssoConfig[0]?.companyCode,
				domain: ssoConfig[0]?.domain,
				xmlContent: ssoConfig[0]?.metadataFile || '',
				ssoConfig,
			});
		} else {
			// If no saved config, initialize with default keys
			const keys = Object.keys(defaultKeys);
			this.formRef.current?.setFieldsValue({ keys });

			// Set default mappings
			for (const [index, key] of keys.entries()) {
				this.formRef.current?.setFieldsValue({
					[`attributeMapping[${index}].key`]: key,
					[`attributeMapping[${index}].value`]: defaultKeys[key],
				});
			}
		}
	}

	add = () => {
		const keys = this.formRef.current?.getFieldValue('keys') || [];
		const nextKeys = keys.concat(keys.length);
		this.formRef.current?.setFieldsValue({
			keys: nextKeys,
		});
	};

	remove = (k) => {
		const keys = this.formRef.current?.getFieldValue('keys') || [];
		if (keys.length === 1) {
			return;
		}

		this.formRef.current?.setFieldsValue({
			keys: keys.filter((key) => key !== k),
		});
		delete defaultKeys[k];
	};

	handleSubmit = async () => {
		try {
			const values = await this.formRef.current?.validateFields();

			const ssoSettings = { companyId: this.props.currentUser.companyId };
			if (values.companyCode) ssoSettings.companyCode = values.companyCode;
			if (values.domain) ssoSettings.domain = values.domain;
			if (this.state.xmlContent)
				ssoSettings.metadataFile = this.state.xmlContent;
			if (values.metadataUrl) ssoSettings.metadataUrl = values.metadataUrl;
			if (values.attributeMapping) {
				ssoSettings.attributeMapping = {};
				Object.entries(values.attributeMapping).map((entry) => {
					ssoSettings.attributeMapping[entry[1].key] = entry?.[1]?.value;
				});
			}

			ssoSettings.type = 'createSSO';
			const response = await SSO(ssoSettings);
			message.success('Changes Saved');
			return response;
		} catch (error) {
			console.log(error);
		}
	};

	handleSubmitUpdateHost = async () => {
		try {
			const values = await this.formRef.current?.validateFields(['domain']);
			const { domain = null } = values;
			const ssoSettings = { companyId: this.props.currentUser.companyId };
			ssoSettings.domain = domain;
			ssoSettings.type = 'updateDomain';
			const response = await SSO(ssoSettings);
			message.success('Host Saved');
			return response;
		} catch (error) {
			console.log(error);
		}
	};

	validateUrl = (rule, url, callback) => {
		if (url) {
			try {
				// Add default scheme if missing
				const hostnameRegex =
					/^(?!-)[A-Za-z\d-]+(\.[A-Za-z\d-]+)*\.[A-Za-z]{2,}$/;

				if (!hostnameRegex.test(url) && url !== 'localhost:3000')
					callback('The input is not a valid host name!');
				const domain = url.replace(/https?:\/\//, '').replace(/\/$/, '');
				if (this.state.domain !== domain) {
					this.setState({ domain });
					this.formRef.current?.setFieldsValue({
						domain,
					});
				}

				callback(); // Validation passed
			} catch {
				callback('The input is not a valid host name!');
			}
		} else {
			callback();
		}
	};

	validateMetadata = (rule, url, callback) => {
		const MetadataURL = this.formRef.current?.getFieldValue(`metadataUrl`);
		const MetadataFile = this.formRef.current?.getFieldValue(`metadataFile`);
		if (!MetadataURL && !this.state.xmlContent && !MetadataFile) {
			callback('Please enter a Metadata URL or upload a Metadata XML File.');
		} else {
			callback();
		}
	};

	handleFileChange = (info) => {
		// Keep track of the fileList in state
		const fileList = [...info.fileList].slice(-1); // Only keep the latest file
		this.setState({ metadataFile: { ...info, fileList } });

		// Handle file content reading
		const file = info.file.originFileObj || info.file;
		if (file instanceof Blob) {
			const reader = new FileReader();
			reader.addEventListener('load', (e) => {
				this.setState({ xmlContent: e.target.result });
			});
			reader.readAsText(file);
		}
	};

	render() {
		const { setFolderName, ssoConfig, xmlContent } = this.state;
		return (
			<>
				<Form
					ref={this.formRef}
					initialValues={{ keys: Object.keys(defaultKeys) }}
				>
					<h4 className="setting-card-title">Host Settings</h4>
					<div className="custom-form-group">
						<label className="custom-label">Custom Host Name: </label>
						<Form.Item
							name="domain"
							initialValue={this.state.domain || null}
							rules={[{ validator: this.validateUrl }]}
						>
							<Input
								className="custom-input"
								onInput={(input) => {
									this.setState({ domain: input.target.value });
								}}
							/>
						</Form.Item>
						<Button
							type="primary"
							size="large"
							className="btn-min-width"
							style={{ marginTop: 15, marginBottom: 10 }}
							onClick={this.handleSubmitUpdateHost}
						>
							Save Host
						</Button>
					</div>
					<h4 className="setting-card-title">SSO Settings</h4>
					<div className="custom-form-group">
						<label className="custom-label">Company Code:</label>
						<Form.Item
							name="companyCode"
							initialValue={this.state.companyCode || null}
							rules={[
								{
									required: true,
									message: 'Please input company code.',
								},
							]}
						>
							<Input
								className="custom-input"
								onInput={(input) =>
									this.setState({ companyCode: input.target.value })
								}
							/>
						</Form.Item>
					</div>

					<h4 className="setting-card-title">Attribute Mapping:</h4>
					<Form.List
						name="attributeMapping"
						initialValue={Object.keys(defaultKeys).map((key, index) => ({
							key,
							value: defaultKeys[key],
						}))}
					>
						{(fields, { add, remove }) => (
							<>
								{fields.map(({ key, name, ...restField }, index) => {
									const attribute = Object.keys(defaultKeys)[index];
									const claim = defaultKeys[attribute];

									return (
										<Space
											key={key}
											style={{ display: 'flex', marginBottom: 8 }}
											align="baseline"
										>
											<Form.Item
												{...restField}
												name={[name, 'key']}
												label="Attribute"
												initialValue={attribute}
												rules={[
													{ required: true, message: 'Missing attribute name' },
												]}
											>
												<Select
													placeholder="Select an attribute"
													style={{ width: 250 }}
												>
													{attributeOptions.map((option) => (
														<Option key={option.id} value={option.id}>
															{option.label}
														</Option>
													))}
												</Select>
											</Form.Item>
											<Form.Item
												{...restField}
												name={[name, 'value']}
												label="Mapping"
												initialValue={claim}
												rules={[{ required: true, message: 'Missing Mapping' }]}
											>
												<Input
													placeholder="SAML Mapping"
													style={{ width: 400 }}
												/>
											</Form.Item>
											{fields.length > 1 ? (
												<MinusCircleOutlined
													className="dynamic-delete-button"
													onClick={() => remove(name)}
												/>
											) : null}
										</Space>
									);
								})}
								<Form.Item>
									<Button type="dashed" onClick={() => add()}>
										<PlusOutlined />
										Add Attribute Mapping
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>
					<h4 className="setting-card-title">Metadata:</h4>
					<div className="custom-form-group">
						<label className="custom-label">Metadata URL: </label>
						<Form.Item
							name="metadataUrl"
							initialValue={null}
							rules={[{ validator: this.validateMetadata }]}
						>
							<Input
								className="custom-input"
								onInput={(input) =>
									this.setState({ metadataUrl: input.target.value })
								}
							/>
						</Form.Item>
					</div>
					<div className="custom-form-group">
						<label className="custom-label">Metadata File:</label>
						<Form.Item
							name="metadataFile"
							rules={[{ validator: this.validateMetadata }]}
						>
							<Upload
								accept=".xml"
								beforeUpload={() => false}
								fileList={
									this.state.metadataFile?.fileList ||
									(ssoConfig?.[0]?.metadataFile
										? [
												{
													uid: '-1',
													name: 'metadata.xml',
													status: 'done',
													url: (() => {
														if (!ssoConfig?.[0]?.metadataFile) return null;
														const xmlBlob = new Blob(
															[ssoConfig?.[0]?.metadataFile],
															{
																type: 'application/xml',
															}
														);
														return URL.createObjectURL(xmlBlob);
													})(),
												},
											]
										: [])
								}
								onChange={(info) => this.handleFileChange(info)}
							>
								<Button icon={<UploadOutlined />}>
									Click to Upload XML File
								</Button>
							</Upload>
						</Form.Item>
					</div>
					<div>
						<Button
							type="primary"
							size="large"
							className="btn-min-width"
							style={{ marginTop: 15, marginBottom: 10 }}
							onClick={this.handleSubmit}
						>
							Save All
						</Button>
					</div>
				</Form>
				{setFolderName ? (
					<Alert
						message="Your updates were successfully submitted."
						type="success"
					/>
				) : null}
			</>
		);
	}
}
export default withApollo(SettingsSSOForm);
